import { Component, CSSProperties, createRef } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/SidebarPopupPlace/SidebarPopupPlace.styl'
import { defaultMemoize } from 'reselect'


const ARROW_OUTER_OFFSET = parseInt(styles.arrowOuterOffset, 10)
const ARROW_INNER_OFFSET = parseInt(styles.arrowInnerOffset, 10)

export type SidebarPopupPlacePosition = 'left' | 'right' | 'fill'

export type SidebarPopupPlaceProps = {
  position: SidebarPopupPlacePosition
  targetRect: ClientRect
}

type SidebarPopupPlaceOffsets = {
  popupTop: number
  arrowTop: number
}

type SidebarPopupPlaceState = {
  place: SidebarPopupPlaceOffsets | null
}

// use it only you have scrollable area inside of popup,
// otherwise you SidebarScrollablePopupPlace
export class SidebarPopupPlace extends Component<SidebarPopupPlaceProps, SidebarPopupPlaceState> {
  state: SidebarPopupPlaceState = {
    place: null,
  }

  private containerRef = createRef<HTMLDivElement>()

  render () {
    const {
      position,
      children,
    } = this.props
    const { place } = this.state

    const rightCorners = position !== 'fill'

    const rootClassName = cn(styles.popupPlace, {
      [styles.fill]: position === 'fill',
      [styles.right]: position === 'right',
      [styles.left]: position === 'left',
      [styles.rightCorners]: rightCorners,
    })

    const positionerStyles: CSSProperties = place
      ? { transform: `translateY(${ place.popupTop }px)` }
      : {}

    const arrowStyles: CSSProperties = {
      top: place ? place.arrowTop : undefined,
    }

    return (
      <div
        className={ rootClassName }
        ref={ this.containerRef }
      >
        <div className={ styles.arrow } style={ arrowStyles } />
        <div
          className={ styles.positioner }
          style={ positionerStyles }
        >
          { children }
        </div>
      </div>
    )
  }

  componentDidMount () {
    this.updatePlace()
  }

  componentDidUpdate (prevProps: SidebarPopupPlaceProps) {
    if (prevProps.targetRect !== this.props.targetRect) {
      this.updatePlace()
    }
  }

  private updatePlace () {
    const { targetRect } = this.props

    if (this.containerRef.current) {
      this.setState({
        place: this.getPlace(targetRect, this.containerRef.current),
      })
    }
  }

  private getPlace = defaultMemoize((
    targetRect: ClientRect,
    containerNode: HTMLDivElement,
  ): SidebarPopupPlaceOffsets => {
    const containerRect = containerNode.getBoundingClientRect()

    const bottomThreshold = targetRect.bottom + 2 * ARROW_OUTER_OFFSET

    const popupTranslateTop = bottomThreshold > containerRect.bottom
      ? bottomThreshold - containerRect.bottom
      : 0

    const arrowSizeAmend = targetRect.height / 2 - ARROW_INNER_OFFSET
    const arrowTop = targetRect.top + arrowSizeAmend - containerRect.top

    return {
      popupTop: popupTranslateTop,
      arrowTop,
    }
  })
}
